import { useState, useEffect } from 'react';
import {
  Calendar, Users, Phone, Mail, MessageSquare, Clock,
  RefreshCw, Send, UserPlus, X, CheckCircle, Archive,
  Search, Save
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { CallHistoryWidget } from './CallHistoryWidget';
import { sendSMS as sendRingCentralSMS } from '../../lib/ringcentralSMS';
import { GoHighLevelService, GHLMessage } from '../../services/goHighLevelService';

interface DailyLead {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  status: string;
  contact_attempts: number;
  last_contacted: string | null;
  last_sms_sent: string | null;
  last_email_sent: string | null;
  is_from_old_pool: boolean;
  date_added: string;
  notes: string | null;
  source: string | null;
}

interface DailyStats {
  total_assigned: number;
  completed: number;
  pending: number;
  archived_today: number;
}

interface DriverStatus {
  id: string;
  name: string;
  color: string;
  order: number;
}

const DEFAULT_STATUSES: DriverStatus[] = [
  { id: 'not_contacted', name: 'Not Contacted', color: 'bg-gray-500', order: 1 },
  { id: 'no_response', name: 'No Response', color: 'bg-slate-500', order: 2 },
  { id: 'answered', name: 'Answered', color: 'bg-teal-500', order: 3 },
  { id: 'processing', name: 'Processing', color: 'bg-blue-500', order: 5 },
  { id: 'approved_drug_test', name: 'Approved, Drug test scheduled', color: 'bg-indigo-500', order: 6 },
  { id: 'hired', name: 'Hired', color: 'bg-green-500', order: 7 },
  { id: 'not_qualified', name: 'Not Qualified', color: 'bg-red-500', order: 8 },
  { id: 'not_interested', name: 'Not Interested', color: 'bg-orange-500', order: 9 },
  { id: 'haitian', name: 'Haitian', color: 'bg-purple-500', order: 10 },
  { id: 'vm', name: 'VM', color: 'bg-fuchsia-500', order: 11 },
  { id: 'other', name: 'Other', color: 'bg-zinc-500', order: 12 },
];

export function RecruiterDailyCRM() {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [currentUserFullName, setCurrentUserFullName] = useState<string>('');

  const [activeTab, setActiveTab] = useState<'daily' | 'streak' | 'reminders' | 'archive'>(() => {
    // Read tab from URL parameter on initial load
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'streak' || tabParam === 'reminders' || tabParam === 'archive') {
      return tabParam;
    }
    return 'daily';
  });

  const [dailyLeads, setDailyLeads] = useState<DailyLead[]>([]);
  const [archivedLeads, setArchivedLeads] = useState<any[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<DailyLead[]>([]);
  const [selectedLead, setSelectedLead] = useState<DailyLead | null>(null);
  const [statuses] = useState<DriverStatus[]>(DEFAULT_STATUSES);
  const [callHistoryMap, setCallHistoryMap] = useState<Record<string, any[]>>({});

  const [stats, setStats] = useState<DailyStats>({
    total_assigned: 0,
    completed: 0,
    pending: 0,
    archived_today: 0
  });

  const [archiveStats, setArchiveStats] = useState({
    total: 0,
    this_week: 0,
    this_month: 0
  });

  const [dailyGoal] = useState(40); // 40 calls per day goal
  const [todayCallCount, setTodayCallCount] = useState(0);
  const [dailyCallHistory, setDailyCallHistory] = useState<Array<{date: string, count: number}>>([]);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [reminders, setReminders] = useState<DailyLead[]>([]);
  const [reminderStats, setReminderStats] = useState({
    total: 0,
    today: 0,
    upcoming: 0
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'contacted'>('all');

  // SMS/Conversation state
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    if (user?.id) {
      fetchCurrentUserName();
    }
    if (user?.email) {
      loadApiKey();
      loadTodayCallCount();
      loadDailyCallHistory();
      loadReminders();
    }
  }, [user]);

  // Once we know who the recruiter is, pull their leads. Re-runs if it changes.
  useEffect(() => {
    if (currentUserFullName) {
      loadDailyLeads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserFullName]);

  useEffect(() => {
    applyFilters();
  }, [dailyLeads, activeTab, searchTerm, filterStatus]);

  const fetchCurrentUserName = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user.id)
      .single();
    if (data?.full_name) setCurrentUserFullName(data.full_name);
  };

  async function loadApiKey() {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'ghl_api_key')
        .maybeSingle();

      if (!error && data?.value) {
        setApiKey(data.value);
      }
    } catch (error) {
      console.error('Error loading API key:', error);
    }
  }

  async function sendSMS() {
    if (!selectedLead || !smsMessage.trim()) return;

    try {
      setSendingSms(true);
      await sendRingCentralSMS(selectedLead.phone, smsMessage);
      
      // Mark as contacted
      await markAsContacted(selectedLead.id, 'sms');
      
      showToast('success', 'SMS sent successfully');
      setSmsMessage('');
    } catch (error) {
      console.error('Error sending SMS:', error);
      showToast('error', 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  }

  const loadDailyCallHistory = async () => {
    try {
      // Get calls for the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0);

      const { data: calls, error } = await supabase
        .from('recruiter_calls')
        .select('start_time')
        .eq('recruiter_email', user?.email)
        .gte('start_time', thirtyDaysAgo.toISOString());

      if (error) throw error;

      // Group calls by date
      const callsByDate: Record<string, number> = {};
      (calls || []).forEach(call => {
        const date = new Date(call.start_time).toISOString().split('T')[0];
        callsByDate[date] = (callsByDate[date] || 0) + 1;
      });

      // Convert to array and sort by date
      const history = Object.entries(callsByDate)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setDailyCallHistory(history);

      // Calculate streaks
      let current = 0;
      let longest = 0;
      let tempStreak = 0;
      const today = new Date().toISOString().split('T')[0];
      
      // Sort chronologically for streak calculation
      const sortedHistory = [...history].sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      for (let i = sortedHistory.length - 1; i >= 0; i--) {
        const entry = sortedHistory[i];
        if (entry.count >= dailyGoal) {
          tempStreak++;
          if (entry.date === today || i === sortedHistory.length - 1) {
            current = tempStreak;
          }
          longest = Math.max(longest, tempStreak);
        } else {
          if (entry.date === today) {
            current = 0;
          }
          tempStreak = 0;
        }
      }

      setCurrentStreak(current);
      setLongestStreak(longest);
    } catch (error) {
      console.error('Error loading daily call history:', error);
    }
  };

  const loadTodayCallCount = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      // Get all calls for today
      const { count, error } = await supabase
        .from('recruiter_calls')
        .select('*', { count: 'exact', head: true })
        .eq('recruiter_email', user?.email)
        .gte('start_time', todayISO);

      if (error) throw error;

      setTodayCallCount(count || 0);
    } catch (error) {
      console.error('Error loading today call count:', error);
    }
  };

  const loadCallHistory = async (leads: DailyLead[]) => {
    try {
      const phones = leads.map(l => l.phone).filter(Boolean);
      if (phones.length === 0) return {};

      const { data: calls, error } = await supabase
        .from('recruiter_calls')
        .select('*')
        .in('candidate_phone', phones)
        .order('start_time', { ascending: false });

      if (error) throw error;

      const historyMap: Record<string, any[]> = {};
      leads.forEach(lead => {
        if (lead.phone) {
          historyMap[lead.phone] = (calls || []).filter(c => c.candidate_phone === lead.phone);
        }
      });

      return historyMap;
    } catch (error) {
      console.error('Error loading call history:', error);
      return {};
    }
  };

  const determineStatusFromCalls = (calls: any[]): string | null => {
    if (!calls || calls.length === 0) return null;

    // Check if any call was accepted/answered
    const hasAccepted = calls.some(c => 
      c.result?.toLowerCase().includes('accept') || 
      c.result?.toLowerCase().includes('answer')
    );

    if (hasAccepted) {
      return 'answered';
    }

    // If there are calls but none accepted, it's no response
    return 'no_response';
  };

  const loadReminders = async () => {
    try {
      // Get reminders for leads assigned to this recruiter
      const { data: assignments, error: assignError } = await supabase
        .from('crm_daily_assignments')
        .select('lead_id')
        .eq('recruiter_email', user?.email);

      if (assignError) throw assignError;

      const leadIds = (assignments || []).map((a: any) => a.lead_id);

      if (leadIds.length === 0) {
        setReminderStats({ total: 0, today: 0, upcoming: 0 });
        return;
      }

      // Get pending reminders for these leads
      const { data: reminderData, error: reminderError } = await supabase
        .from('crm_reminders')
        .select(`
          *,
          crm_leads!inner(
            id,
            first_name,
            last_name,
            phone,
            email,
            status
          )
        `)
        .in('contact_id', leadIds)
        .eq('status', 'pending')
        .order('priority', { ascending: true })
        .order('due_date', { ascending: true });

      if (reminderError) throw reminderError;

      const remindersWithLeads = (reminderData || []).map((r: any) => ({
        ...r.crm_leads,
        reminder: r
      }));

      setReminders(remindersWithLeads as any);

      // Calculate stats
      const today = new Date().toISOString().split('T')[0];
      const todayReminders = (reminderData || []).filter((r: any) => 
        r.due_date?.startsWith(today)
      );
      const upcomingReminders = (reminderData || []).filter((r: any) => 
        r.due_date && r.due_date > today
      );

      setReminderStats({
        total: reminderData?.length || 0,
        today: todayReminders.length,
        upcoming: upcomingReminders.length
      });
    } catch (error) {
      console.error('Error loading reminders:', error);
      setReminderStats({ total: 0, today: 0, upcoming: 0 });
    }
  };

  // Pull every crm_lead where this recruiter is named on the lead itself.
  // The old assignment-batch flow is gone — recruiters now see leads as soon
  // as someone in the Leads page assigns their name.
  const loadDailyLeads = async () => {
    try {
      setLoading(true);

      const leadsData = await fetchAllRows<any>(() => supabase
        .from('crm_leads')
        .select('*')
        .eq('recruiter', currentUserFullName)
        .not('status', 'in', '(archived,hired)')
        .order('created_at', { ascending: false }));

      const daily: DailyLead[] = (leadsData || []).map((l: any) => ({
        id: l.id,
        first_name: l.first_name || '',
        last_name: l.last_name || '',
        phone: l.phone || '',
        email: l.email || '',
        status: l.status,
        contact_attempts: l.contact_attempts || 0,
        last_contacted: l.last_contacted,
        last_sms_sent: l.last_sms_sent ?? null,
        last_email_sent: l.last_email_sent ?? null,
        // Pseudo "old vs new" split: anything not still in 'new' status is
        // already in flight, so treat it as the existing-pool side.
        is_from_old_pool: l.status !== 'new',
        date_added: l.created_at,
        notes: l.notes,
        source: l.source,
      }));

      // Load call history then suggest a status when the calls clearly tell
      // a story (answered, repeatedly unanswered, etc.).
      const dailyCallHistory = await loadCallHistory(daily);
      setCallHistoryMap(prev => ({ ...prev, ...dailyCallHistory }));

      for (const lead of daily) {
        const calls = dailyCallHistory[lead.phone] || [];
        const suggestedStatus = determineStatusFromCalls(calls);
        if (suggestedStatus && (lead.status === 'processing' || lead.status === 'not_contacted')) {
          await supabase.from('crm_leads').update({ status: suggestedStatus }).eq('id', lead.id);
          lead.status = suggestedStatus;
        }
      }

      setDailyLeads(daily);

      // Archived (still recruiter-scoped via the legacy email column where
      // available; falls back to none if the archive table isn't populated
      // for this recruiter yet).
      const { data: archived, error: archiveError } = await supabase
        .from('crm_archived_leads')
        .select(`*, crm_leads (*)`)
        .eq('recruiter_email', user?.email)
        .order('archived_at', { ascending: false });

      if (!archiveError && archived) {
        setArchivedLeads(archived);
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        setArchiveStats({
          total: archived.length,
          this_week: archived.filter(a => new Date(a.archived_at) >= weekAgo).length,
          this_month: archived.filter(a => new Date(a.archived_at) >= monthAgo).length,
        });
      }

      const completed = daily.filter(l => l.contact_attempts > 0).length;
      const pending = daily.filter(l => l.contact_attempts === 0).length;

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const { count: archivedToday } = await supabase
        .from('crm_archived_leads')
        .select('*', { count: 'exact', head: true })
        .eq('recruiter_email', user?.email)
        .gte('archived_at', todayStart.toISOString());

      setStats({
        total_assigned: daily.length,
        completed,
        pending,
        archived_today: archivedToday || 0,
      });
    } catch (error) {
      console.error('Error loading daily leads:', error);
      showToast('error', 'Failed to load daily leads');
    } finally {
      setLoading(false);
    }
  };

  const markAsContacted = async (leadId: string, contactType: 'call' | 'sms' | 'email') => {
    try {
      const { error } = await supabase.rpc('mark_lead_contacted', {
        p_lead_id: leadId,
        p_contact_type: contactType
      });

      if (error) throw error;

      showToast('success', `Lead marked as ${contactType === 'call' ? 'called' : contactType === 'sms' ? 'texted' : 'emailed'}`);
      await loadDailyLeads();
    } catch (error) {
      console.error('Error marking lead as contacted:', error);
      showToast('error', 'Failed to update lead');
    }
  };

  async function updateLeadStatus(leadId: string, newStatus: string) {
    try {
      const { error } = await supabase
        .from('crm_leads')
        .update({ status: newStatus })
        .eq('id', leadId);

      if (error) throw error;
      
      setDailyLeads(dailyLeads.map(l => 
        l.id === leadId ? { ...l, status: newStatus } : l
      ));
      
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
      
      showToast('success', 'Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('error', 'Failed to update status');
    }
  }

  async function updateLeadNotes(leadId: string, notes: string) {
    try {
      const { error } = await supabase
        .from('crm_leads')
        .update({ 
          notes,
          last_contacted: new Date().toISOString()
        })
        .eq('id', leadId);

      if (error) throw error;
      
      setDailyLeads(dailyLeads.map(l => 
        l.id === leadId ? { ...l, notes, last_contacted: new Date().toISOString() } : l
      ));
      
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, notes, last_contacted: new Date().toISOString() });
      }
      
      showToast('success', 'Notes saved successfully');
    } catch (error) {
      console.error('Error updating notes:', error);
      showToast('error', 'Failed to save notes');
    }
  }

  function getDateLabel(dateString: string): string {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Reset hours for comparison
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    if (date.getTime() === today.getTime()) {
      return 'Today';
    } else if (date.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  }

  function groupLeadsByDate(leads: DailyLead[]) {
    const grouped: Record<string, DailyLead[]> = {};
    
    leads.forEach(lead => {
      const dateKey = lead.date_added ? new Date(lead.date_added).toDateString() : 'Unknown';
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(lead);
    });
    
    // Sort by date (most recent first)
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return new Date(b).getTime() - new Date(a).getTime();
    });
    
    const result: Record<string, DailyLead[]> = {};
    sortedKeys.forEach(key => {
      result[key] = grouped[key];
    });
    
    return result;
  }

  const changeTab = (tab: 'daily' | 'streak' | 'reminders' | 'archive') => {
    setActiveTab(tab);
    // Update URL without page reload
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.pushState({}, '', url.toString());
  };

  function applyFilters() {
    let source = activeTab === 'daily' ? dailyLeads : [];
    let filtered = [...source];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(l => 
        l.first_name?.toLowerCase().includes(search) ||
        l.last_name?.toLowerCase().includes(search) ||
        l.phone?.toLowerCase().includes(search) ||
        l.email?.toLowerCase().includes(search)
      );
    }

    if (filterStatus === 'pending') {
      filtered = filtered.filter(l => l.contact_attempts === 0);
    } else if (filterStatus === 'contacted') {
      filtered = filtered.filter(l => l.contact_attempts > 0);
    }

    setFilteredLeads(filtered);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header — matches LeadsView / AllDriversView visual language */}
      <div className="max-w-[1600px] mx-auto p-6 pb-0">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Daily CRM</h1>
                <p className="text-slate-600">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>

          {/* Simple counter: leads updated today */}
          <div className="mt-6 flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 px-5 py-4">
            <div className="text-3xl font-bold text-slate-900">{stats.completed}</div>
            <div className="text-sm text-slate-600">Leads Updated Today</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-[1600px] mx-auto px-6 pt-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-1 px-2 py-2">
            {([
              { id: 'daily',     label: 'Daily Leads', icon: Calendar, accent: 'blue',   count: stats.total_assigned, countLabel: 'assigned' },
              { id: 'streak',    label: 'Streak',      icon: Calendar, accent: 'orange', count: currentStreak,        countLabel: 'day streak' },
              { id: 'reminders', label: 'Reminders',   icon: Clock,    accent: 'purple', count: reminderStats.total,  countLabel: 'scheduled' },
              { id: 'archive',   label: 'Archive',     icon: Archive,  accent: 'slate',  count: archiveStats.total,   countLabel: 'archived' },
            ] as const).map(t => {
              const Icon = t.icon;
              const isActive = activeTab === t.id;
              const accentBg = {
                blue: 'bg-blue-100 text-blue-700',
                orange: 'bg-orange-100 text-orange-700',
                purple: 'bg-purple-100 text-purple-700',
                slate: 'bg-slate-200 text-slate-700',
              }[t.accent];
              return (
                <button
                  key={t.id}
                  onClick={() => changeTab(t.id)}
                  className={`flex-1 flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                    isActive ? `${accentBg} font-semibold` : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <div className="text-left">
                    <div className="text-sm">{t.label}</div>
                    <div className="text-[10px] opacity-75">{t.count} {t.countLabel}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-6 py-6">
        {/* Compact Stats Bar - Dynamic based on active tab */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {activeTab === 'daily' && (
            <>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                <div className="p-3 bg-blue-600 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_assigned}</p>
                  <p className="text-xs text-slate-600 font-medium">Assigned to you</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
                <div className="p-3 bg-green-600 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                  <p className="text-xs text-slate-600 font-medium">
                    {stats.total_assigned > 0 ? Math.round((stats.completed / stats.total_assigned) * 100) : 0}% completed
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200">
                <div className="p-3 bg-orange-600 rounded-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
                  <p className="text-xs text-slate-600 font-medium">To contact</p>
                </div>
              </div>

            </>
          )}

          {activeTab === 'archive' && (
            <>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
                <div className="p-3 bg-slate-600 rounded-lg">
                  <Archive className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{archiveStats.total}</p>
                  <p className="text-xs text-slate-600 font-medium">Total archived</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                <div className="p-3 bg-blue-600 rounded-lg">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{archiveStats.this_week}</p>
                  <p className="text-xs text-slate-600 font-medium">This week</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                <div className="p-3 bg-purple-600 rounded-lg">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{archiveStats.this_month}</p>
                  <p className="text-xs text-slate-600 font-medium">This month</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200">
                <div className="p-3 bg-orange-600 rounded-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-600">{stats.archived_today}</p>
                  <p className="text-xs text-slate-600 font-medium">Today</p>
                </div>
              </div>
            </>
          )}

          {activeTab === 'streak' && (
            <>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200">
                <div className="p-3 bg-orange-600 rounded-lg">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-600">{currentStreak}</p>
                  <p className="text-xs text-slate-600 font-medium">Current streak</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200">
                <div className="p-3 bg-yellow-600 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{longestStreak}</p>
                  <p className="text-xs text-slate-600 font-medium">Longest streak</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                <div className="p-3 bg-blue-600 rounded-lg">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{todayCallCount}</p>
                  <p className="text-xs text-slate-600 font-medium">Calls today</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
                <div className="p-3 bg-green-600 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{dailyGoal}</p>
                  <p className="text-xs text-slate-600 font-medium">Daily goal</p>
                </div>
              </div>
            </>
          )}
          </div>
        </div>

        {/* Search and Filters - Modern Design (hidden for streak tab) */}
        {activeTab !== 'streak' && (
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all"
            />
          </div>
          
          <div className="flex gap-2 bg-white rounded-xl p-1 shadow-sm border border-slate-200">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-5 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'all'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              All <span className="ml-1 text-xs opacity-75">({activeTab === 'daily' ? dailyLeads.length : archivedLeads.length})</span>
            </button>
            <button
              onClick={() => setFilterStatus('pending')}
              className={`px-5 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'pending'
                  ? 'bg-orange-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Pending <span className="ml-1 text-xs opacity-75">({stats.pending})</span>
            </button>
            <button
              onClick={() => setFilterStatus('contacted')}
              className={`px-5 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'contacted'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Contacted <span className="ml-1 text-xs opacity-75">({stats.completed})</span>
            </button>
          </div>
        </div>
        )}

        {/* Leads Table / Streak View */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900">
              {activeTab === 'daily' && 'Today\'s Leads'}
              {activeTab === 'archive' && 'Archived Leads'}
              {activeTab === 'streak' && 'Daily Call History (Last 30 Days)'}
            </h2>
          </div>

          {activeTab === 'streak' ? (
            dailyCallHistory.length === 0 ? (
              <div className="p-12 text-center">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-lg text-slate-600 mb-2">No call history yet</p>
                <p className="text-sm text-slate-500">Start making calls to track your streak!</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Calls Made</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Goal Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyCallHistory.map((day) => {
                    const percentage = Math.min((day.count / dailyGoal) * 100, 100);
                    const goalMet = day.count >= dailyGoal;
                    const isToday = day.date === new Date().toISOString().split('T')[0];
                    
                    return (
                      <tr key={day.date} className={`border-b border-slate-100 ${isToday ? 'bg-blue-50' : 'hover:bg-slate-50'} transition-colors`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-slate-900'}`}>
                              {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                              {isToday && <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">Today</span>}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-2xl font-bold ${goalMet ? 'text-green-600' : 'text-orange-600'}`}>
                            {day.count}
                          </span>
                          <span className="text-sm text-slate-500 ml-2">calls</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                              <div 
                                className={`h-full transition-all ${goalMet ? 'bg-green-500' : 'bg-orange-500'}`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium text-slate-600 min-w-[60px]">
                              {day.count}/{dailyGoal}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {goalMet ? (
                            <span className="flex items-center gap-2 text-green-600 font-semibold">
                              <CheckCircle className="w-5 h-5" />
                              Goal Met! 🎉
                            </span>
                          ) : (
                            <span className="flex items-center gap-2 text-orange-600">
                              <Clock className="w-5 h-5" />
                              {dailyGoal - day.count} more needed
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : (activeTab === 'archive' ? archivedLeads.length === 0 : filteredLeads.length === 0) ? (
            <div className="p-12 text-center">
              {activeTab === 'archive' ? <Archive className="w-16 h-16 text-slate-300 mx-auto mb-4" /> : <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />}
              <p className="text-lg text-slate-600 mb-2">
                {activeTab === 'daily' && 'No daily leads yet'}
                {activeTab === 'archive' && 'No archived leads'}
              </p>
              <p className="text-sm text-slate-500">
                {activeTab === 'daily' && stats.total_assigned === 0 && 'When someone assigns you as the recruiter on a lead in the Leads page, it will appear here automatically.'}
                {activeTab === 'daily' && stats.total_assigned > 0 && 'Try adjusting your filters'}
                {activeTab === 'archive' && 'Leads will auto-archive after 7 days of no response'}
              </p>
            </div>
          ) : activeTab === 'archive' ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Archived Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Last Contact</th>
                </tr>
              </thead>
              <tbody>
                {archivedLeads.map((archived: any) => (
                  <tr
                    key={archived.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-400 text-white font-bold text-sm">
                          {archived.crm_leads?.first_name?.charAt(0)}{archived.crm_leads?.last_name?.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {archived.crm_leads?.first_name} {archived.crm_leads?.last_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-0.5">
                        {archived.crm_leads?.phone && (
                          <div className="text-sm text-slate-600 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {archived.crm_leads.phone}
                          </div>
                        )}
                        {archived.crm_leads?.email && (
                          <div className="text-xs text-slate-500 truncate max-w-xs">
                            {archived.crm_leads.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md text-xs font-medium">
                        {archived.archive_reason === 'no_response_7_days' ? 'No response (7 days)' : archived.archive_reason}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">
                        {new Date(archived.archived_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">
                        {archived.last_contact_attempt ? (
                          new Date(archived.last_contact_attempt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Attempts</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Last Contacted</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupLeadsByDate(filteredLeads)).map(([dateKey, leadsInDate]) => (
                  <>
                    {/* Date Header Row */}
                    <tr key={`date-${dateKey}`} className="bg-gradient-to-r from-slate-100 to-slate-50 border-y border-slate-200">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Calendar className="w-4 h-4 text-slate-600" />
                            <span className="font-semibold text-slate-900">
                              {getDateLabel(dateKey)}
                            </span>
                            <span className="text-xs text-slate-500 bg-slate-200 px-2 py-1 rounded-full">
                              {leadsInDate.length} {leadsInDate.length === 1 ? 'lead' : 'leads'}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Leads for this date */}
                    {leadsInDate.map((lead) => (
                      <tr
                        key={lead.id}
                        onClick={() => setSelectedLead(lead)}
                        className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                              lead.contact_attempts > 0 ? 'bg-green-600' : 'bg-orange-600'
                            }`}>
                              {lead.first_name?.charAt(0)}{lead.last_name?.charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-slate-900">
                                {lead.first_name} {lead.last_name}
                              </div>
                              <div className="text-xs text-slate-500">{lead.source || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-0.5">
                            {lead.phone && (
                              <a 
                                href={`tel:${lead.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <Phone className="w-3 h-3" />
                                {lead.phone}
                              </a>
                            )}
                            {lead.email && (
                              <div className="text-xs text-slate-600 truncate max-w-xs flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {lead.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const status = statuses.find(s => s.id === lead.status || s.name === lead.status);
                            return status ? (
                              <span className={`${status.color} text-white px-3 py-1.5 rounded-md text-xs font-medium inline-block`}>
                                {status.name}
                              </span>
                            ) : (
                              <span className="bg-gray-500 text-white px-3 py-1.5 rounded-md text-xs font-medium inline-block">
                                {lead.status || 'Unknown'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          {lead.is_from_old_pool ? (
                            <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                              Old
                            </span>
                          ) : (
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                              New
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const calls = callHistoryMap[lead.phone] || [];
                            const callCount = calls.length;
                            const hasAccepted = calls.some(c => 
                              c.result?.toLowerCase().includes('accept') || 
                              c.result?.toLowerCase().includes('answer')
                            );
                            
                            return (
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${
                                  hasAccepted ? 'text-green-600' : callCount > 0 ? 'text-orange-600' : 'text-slate-600'
                                }`}>
                                  {callCount}
                                </span>
                                {hasAccepted && (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">
                            {lead.last_contacted ? (
                              new Date(lead.last_contacted).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) + ' ' + 
                              new Date(lead.last_contacted).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Right Sidebar - Lead Details */}
      {selectedLead && (
        <>
          <div 
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300"
            onClick={() => setSelectedLead(null)}
          />
          
          <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[600px] max-w-[600px] min-w-[400px] bg-white shadow-2xl transform transition-transform duration-500 ease-in-out flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10 flex h-12 items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span>Lead Details</span>
              </div>
              <button
                onClick={() => setSelectedLead(null)}
                className="flex items-center justify-center rounded-md p-1 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Lead Header */}
            <div className="border-b border-slate-200 bg-white pb-4 pl-4 pr-4 pt-2">
              <div className="flex w-full flex-col items-start justify-center bg-white py-1">
                <div className="text-lg font-medium leading-normal text-slate-900 w-full">
                  <div className="flex w-full flex-row justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 rounded-full p-2">
                        <UserPlus className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">
                          {selectedLead.first_name} {selectedLead.last_name}
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {selectedLead.source || 'Daily Assignment'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-start justify-start gap-1 mt-2">
                  <div className="flex flex-wrap items-center justify-start gap-2">
                    {(() => {
                      const status = statuses.find(s => s.id === selectedLead.status || s.name === selectedLead.status);
                      return status ? (
                        <span className="inline-flex items-center gap-1 text-xs font-normal bg-blue-50 text-blue-700 px-2 py-1 rounded">
                          {status.name}
                        </span>
                      ) : null;
                    })()}
                    {selectedLead.email && (
                      <span className="text-xs text-slate-600 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {selectedLead.email}
                      </span>
                    )}
                    {selectedLead.phone && (
                      <span className="text-xs text-slate-600 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {selectedLead.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 space-y-4">
                {/* Contact Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedLead.email && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-600 rounded-lg p-2">
                          <Mail className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-600 mb-1">Email Address</p>
                          <a href={`mailto:${selectedLead.email}`} className="text-sm font-medium text-blue-600 hover:text-blue-700 truncate block">
                            {selectedLead.email}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedLead.phone && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-100">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-600 rounded-lg p-2">
                          <Phone className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-slate-600 mb-1">Phone Number</p>
                          <a href={`tel:${selectedLead.phone}`} className="text-sm font-medium text-green-600 hover:text-green-700">
                            {selectedLead.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Call History */}
                {selectedLead.phone && (
                  <CallHistoryWidget 
                    candidatePhone={selectedLead.phone} 
                    compact={true}
                  />
                )}

                {/* Conversation */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Conversation
                    </h3>
                  </div>
                  <div className="bg-slate-50 p-4 h-80 overflow-y-auto">
                    {selectedLead.ghl_data ? (
                      <div className="space-y-2 text-xs">
                        {Object.entries(selectedLead.ghl_data).map(([key, value]) => (
                          <div key={key} className="bg-white p-2 rounded border border-slate-200">
                            <span className="font-medium text-slate-700">{key.replace(/_/g, ' ').toUpperCase()}:</span>{' '}
                            <span className="text-slate-600">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-sm text-slate-500 italic">No messages yet. Start the conversation below!</p>
                      </div>
                    )}
                  </div>
                  <div className="bg-white border-t border-slate-200 p-4">
                    <div className="space-y-2">
                      <textarea
                        value={smsMessage}
                        onChange={(e) => setSmsMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !sendingSms && sendSMS()}
                        placeholder="Type your message here..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                        rows={2}
                        disabled={sendingSms}
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">📱 To: {selectedLead.phone} (via RingCentral)</span>
                        <button
                          onClick={sendSMS}
                          disabled={sendingSms || !smsMessage.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition-colors"
                        >
                          <Send className="w-4 h-4" />
                          Send
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Update */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-slate-600" />
                    Update Status
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {statuses.map(status => (
                      <button
                        key={status.id}
                        onClick={() => updateLeadStatus(selectedLead.id, status.id)}
                        className={`${status.color} text-white px-3 py-1.5 rounded-md hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all text-xs font-medium relative`}
                      >
                        {status.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
                    <Save className="w-4 h-4 text-slate-600" />
                    Notes
                  </h3>
                  <textarea
                    value={selectedLead.notes || ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, notes: e.target.value })}
                    onBlur={(e) => updateLeadNotes(selectedLead.id, e.target.value)}
                    className="w-full px-3 py-2 bg-white border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                    rows={3}
                    placeholder="Add notes about this contact..."
                  />
                </div>
              </div>
            </div>
            
            {/* Footer - Save & Close Button */}
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setSelectedLead(null)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl bg-slate-900"
              >
                <Save className="w-4 h-4" />
                Save & Close
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
